import React, { Children, isValidElement, createElement } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { matchPath, withRouter } from 'react-router';
import { Link } from 'react-router-dom';

import { Div, Text } from '@pinpoint/design-kit';

import { capitalize } from 'src/functions';
import { connect } from 'react-redux';

const BaseBreadcrumbsView = styled(Div)`
  display: flex;
  padding: 0.5rem 0rem;
  height: 100%;
  align-items: center;
  > div {
    display: flex;
    text-align: center;
    align-items: center;
    justify: center;
  }
  > div:not(:last-child)::after {
    content: '/';
    color: ${(props) => props.color};
    padding: 0rem 1rem;
    line-height: 1.75rem;
  }
`;

export const BreadcrumbsLink = styled(Link)`
  text-decoration: none;

  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

export const BreadcrumbsText = styled(Text)`
  font-style: italic;
  font-weight: 100;
`;

export function BreadcrumbsView({ color, children }) {
  return <BaseBreadcrumbsView color={color}>{children}</BaseBreadcrumbsView>;
}

BreadcrumbsView.propTypes = {
  color: PropTypes.string,
};

BreadcrumbsView.defaultProps = {
  color: 'gray',
};

export function BreadcrumbRoute() {
  return null;
}

export function DefaultBreadcrumb({ pathname }) {
  const crumbs = pathname.split('/').filter((x) => !!x);
  return (
    <BreadcrumbsView>
      {crumbs.map((crumb, index) =>
        index !== crumbs.length - 1 ? (
          <Div>
            <BreadcrumbsLink to={`/${crumbs.slice(0, index + 1).join('/')}`}>{capitalize(crumb)}</BreadcrumbsLink>
          </Div>
        ) : (
          <Div>
            <BreadcrumbsText>{capitalize(crumb)}</BreadcrumbsText>
          </Div>
        )
      )}
    </BreadcrumbsView>
  );
}

DefaultBreadcrumb.propTypes = {
  pathname: PropTypes.string.isRequired,
};

function BreadcrumbSwitchComponent({ children, ...rest }) {
  const {
    role,
    siteRole,
    location: { pathname },
  } = rest;
  let element;
  let matched = false;
  let allowed = true;
  Children.forEach(children, (child) => {
    if (!matched && isValidElement(child)) {
      element = child;
      const { path, allowedRoles = [], deniedRoles = [] } = child.props;
      matched = path && matchPath(pathname, { ...child.props });
      allowed = allowedRoles.length ? allowedRoles.includes(role) || allowedRoles.includes(siteRole) : allowed;
      allowed = deniedRoles.length ? !deniedRoles.includes(role) && !deniedRoles.includes(siteRole) : allowed;
    }
  });
  const props = {
    ...rest,
    pathname,
    match: matched,
  };

  if (matched && !allowed) return null;

  return matched && element.props.component ? (
    createElement(element.props.component, props)
  ) : (
    <DefaultBreadcrumb pathname={pathname} />
  );
}

const mapStateToProps = ({
  app: {
    profile: { role, siteRole },
  },
}) => ({ role, siteRole });

export const BreadcrumbSwitch = connect(mapStateToProps)(withRouter(BreadcrumbSwitchComponent));

export function RouteSwitchComponent({ location, children }) {
  const { pathname } = location;
  let element;
  let matched = false;
  Children.forEach(children, (child) => {
    if (!matched && isValidElement(child)) {
      element = child;
      const { path } = child.props;
      matched = path && matchPath(pathname, { ...child.props });
    }
  });
  return matched ? createElement(element.props.component, { location }) : null;
}

export const RouteSwitch = withRouter(RouteSwitchComponent);

export function RouteCase() {
  return null;
}
